// Builds a unified list of comparison rows from current + proposed snapshots.
// Used by ContentComparisonView for EDIT proposals (3-column grid).
// Each row is one of:
//   { type: 'section', title }
//   { type: 'field',   label, currentValue, proposedValue, isChanged }
import { fmtLocalDate, formatDateRanges, summarizeDates } from '../../../utils/dateUtils';
import { SCHEDULE_TYPE_LABEL } from '../../../constants/gameSchedule';
import { clickLinkFromPortal } from '../../../utils/adPortal';

function section(title) {
  return { type: 'section', title };
}

function field(label, rawCurrent, rawProp) {
  const c = rawCurrent == null ? null : String(rawCurrent);
  const p = rawProp    == null ? null : String(rawProp);
  return { type: 'field', label, currentValue: c, proposedValue: p, isChanged: c !== p };
}

// ── Localisation helpers ─────────────────────────────────────────────────────

// Handles both LocalizedPairs { key, values:[{localeId,value}] } and raw [{localeId,value}].
function toLocaleEntries(d) {
  if (!d) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.values)) return d.values;
  return [];
}

// One row per locale (union of both sides). Only locales that differ are flagged changed.
function localizedRows(label, currentData, proposedData) {
  const curr = toLocaleEntries(currentData);
  const prop = toLocaleEntries(proposedData);

  const seenLocales = new Set();
  const ordered = [];
  for (const e of [...curr, ...prop]) {
    if (e?.localeId && !seenLocales.has(e.localeId)) {
      seenLocales.add(e.localeId);
      ordered.push(e.localeId);
    }
  }

  return ordered.map((localeId) => {
    const lbl  = `${label} (${localeId.slice(0, 2).toUpperCase()})`;
    const cVal = curr.find((e) => e.localeId === localeId)?.value ?? null;
    const pVal = prop.find((e) => e.localeId === localeId)?.value ?? null;
    return field(lbl, cVal, pVal);
  });
}

// MediaData shape: { linkLocalizations: [{localeId, mediaLink}] }
// One row per locale — compares mediaLink strings directly.
function mediaRows(label, currentMedia, proposedMedia) {
  const toLinks = (m) =>
    Array.isArray(m?.linkLocalizations) ? m.linkLocalizations : [];

  const curr = toLinks(currentMedia);
  const prop = toLinks(proposedMedia);

  const seenLocales = new Set();
  const ordered = [];
  for (const e of [...curr, ...prop]) {
    if (e?.localeId && !seenLocales.has(e.localeId)) {
      seenLocales.add(e.localeId);
      ordered.push(e.localeId);
    }
  }

  if (!ordered.length) return [];

  return ordered.map((localeId) => {
    const lbl  = `${label} (${localeId.slice(0, 2).toUpperCase()})`;
    const cVal = curr.find((e) => e.localeId === localeId)?.mediaLink ?? null;
    const pVal = prop.find((e) => e.localeId === localeId)?.mediaLink ?? null;
    return field(lbl, cVal, pVal);
  });
}

// ── Scalar helpers ───────────────────────────────────────────────────────────

function boolStr(val) {
  return val == null ? null : (val ? 'Yes' : 'No');
}

function liveRecordedStr(val) {
  return val == null ? null : (val ? 'Live' : 'Recorded');
}

function enabledDisabledStr(val) {
  return val == null ? null : (val ? 'Enabled' : 'Disabled');
}

// Event scheduledStart/scheduledEnd are full ISO datetimes (unlike the plain date-only
// strings fmtLocalDate expects), so they need their own formatter that keeps the time.
function fmtEventDateTime(val) {
  if (!val) return null;
  try {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  } catch {
    return String(val);
  }
}

function fmtDate(val) {
  if (!val) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(new Date(val));
  } catch { return String(val); }
}

// ── Lookup resolver ──────────────────────────────────────────────────────────
// Resolves an integer FK id to a human-readable name using a preloaded map.
// Falls back to String(id) when the map has no entry for that id.
function resolve(map, id) {
  if (id == null) return null;
  if (map && map[id] != null) return map[id];
  return String(id);
}

// ── Tax helper ───────────────────────────────────────────────────────────────
// Current (live) snapshots nest tax as { tax: { categoryId, name, ratePct, isInclusive } }.
// Proposed snapshots (built from the edit form) store it flat as taxCategoryId/taxIsInclusive.
// Both shapes are supported here so the comparison reads correctly regardless of source.
function taxCategoryId(data) {
  return data?.taxCategoryId ?? data?.tax?.categoryId ?? null;
}

function taxCategoryName(data, taxCategoryMap) {
  if (data?.tax?.name) return data.tax.name;
  return resolve(taxCategoryMap, taxCategoryId(data));
}

function taxMode(data) {
  if (!data) return null;
  const v = data.taxIsInclusive !== undefined ? data.taxIsInclusive : data.tax?.isInclusive;
  return v == null ? null : (v ? 'Inclusive' : 'Exclusive');
}

function taxRate(data, taxCategories) {
  if (data?.tax?.ratePct != null) return `${data.tax.ratePct}%`;
  const id = taxCategoryId(data);
  const cat = (taxCategories ?? []).find((t) => t.id === id);
  return cat?.ratePct != null ? `${cat.ratePct}%` : null;
}

// ── Price rows ───────────────────────────────────────────────────────────────
// ItemPriceOut only has { baseCost (minor units), currencyId }.
// currencyMap: { [currencyId]: 'USD' | 'EUR' | … } — derived from getCurrencies().
function priceRows(currentPrices, proposedPrices, currencyMap) {
  const cArr = Array.isArray(currentPrices)  ? currentPrices  : [];
  const pArr = Array.isArray(proposedPrices) ? proposedPrices : [];

  const seenIds = new Set();
  const ordered = [];
  for (const p of [...cArr, ...pArr]) {
    if (p?.currencyId != null && !seenIds.has(p.currencyId)) {
      seenIds.add(p.currencyId);
      ordered.push(p.currencyId);
    }
  }

  return ordered.map((id) => {
    const c  = cArr.find((p) => p.currencyId === id);
    const pp = pArr.find((p) => p.currencyId === id);
    const fmt = (minor) => minor != null ? String(minor) : null;
    const label = currencyMap?.[id] ? `Price (${currencyMap[id]})` : `Price (currency ${id})`;
    return field(label, fmt(c?.baseCost), fmt(pp?.baseCost));
  });
}

// ── Portal position name helpers ─────────────────────────────────────────────
// Matches getPositionLabels(3) in PortalShared.jsx: [0]='Left', [1]='Center', [2]='Right'; [3]='Back' is only on a 3D ad's portal
const PORTAL_POSITION_NAMES = ['Left', 'Center', 'Right', 'Back'];
function portalPositionName(positionId) {
  return PORTAL_POSITION_NAMES[positionId] != null
    ? PORTAL_POSITION_NAMES[positionId]
    : `Position ${positionId + 1}`;
}

// ── Per-type builders ────────────────────────────────────────────────────────

function buildBoothRows(c, p) {
  return [
    section('IDENTITY'),
    ...localizedRows('Name', c?.localizedPairs, p?.localizedPairs),
    field('Map Spot ID', c?.mapSpotId, p?.mapSpotId),
  ];
}

// One URL row plus thumbnail rows per link position, for a portal on either side (a booth portal or a 3D ad's portal).
function portalLinkRows(c, p) {
  const seen = new Set();
  const keys = [];
  for (const l of [...(c?.portalLinks ?? []), ...(p?.portalLinks ?? [])]) {
    const key = l?.positionId ?? l?.id;
    if (key != null && !seen.has(key)) { seen.add(key); keys.push(key); }
  }
  keys.sort((a, b) => a - b);

  return keys.flatMap((key) => {
    const cl  = (c?.portalLinks ?? []).find((l) => (l.positionId ?? l.id) === key);
    const pl  = (p?.portalLinks ?? []).find((l) => (l.positionId ?? l.id) === key);
    const pos = cl?.positionId ?? pl?.positionId ?? key;
    const posName = portalPositionName(pos);
    return [
      field(`${posName} URL`, cl?.externalLink ?? null, pl?.externalLink ?? null),
      ...mediaRows(`${posName} Thumbnail`, cl?.thumbnailMedia, pl?.thumbnailMedia),
    ];
  });
}

function buildPortalRows(c, p, lookups) {
  const portalTypeName = (id) => resolve(lookups?.portalType, id);
  const linkRows = portalLinkRows(c, p);

  return [
    section('IDENTITY'),
    ...localizedRows('Title', c?.localizedPairs, p?.localizedPairs),
    field('Portal Type', portalTypeName(c?.portalTypeId), portalTypeName(p?.portalTypeId)),
    field('Booth ID',    c?.boothId,      p?.boothId),
    field('Event ID',   c?.eventId,       p?.eventId),
    ...(c?.correspondingMedia || p?.correspondingMedia ? [
      section('MAIN MEDIA'),
      ...mediaRows('Media URL', c?.correspondingMedia, p?.correspondingMedia),
    ] : []),
    ...(linkRows.length > 0 ? [section('PORTAL LINKS'), ...linkRows] : []),
  ];
}

function buildCharityRows(c, p) {
  return [
    section('IDENTITY'),
    ...localizedRows('Name',        c?.localizedName, p?.localizedName),
    ...localizedRows('Description', c?.localizedDescription, p?.localizedDescription),
    // field('Active', boolStr(c?.isActive), boolStr(p?.isActive)), // hidden for now
    section('LINKS'),
    field('Website URL',  c?.websiteUrl,  p?.websiteUrl),
    field('Donation URL', c?.donationUrl, p?.donationUrl),
    ...(c?.media || p?.media ? [
      section('MEDIA'),
      ...mediaRows('Image', c?.media, p?.media),
    ] : []),
  ];
}

function buildHostingRows(c, p) {
  return [
    section('IDENTITY'),
    ...localizedRows('Name', c?.localizedName, p?.localizedName),
    field('Stream URL', c?.streamUrl, p?.streamUrl),
    // field('Active', boolStr(c?.isActive), boolStr(p?.isActive)), // hidden for now
  ];
}

function buildAdvertiserRows(c, p) {
  return [
    section('IDENTITY'),
    ...localizedRows('Name', c?.localizedName, p?.localizedName),
    field('Industry', c?.industry, p?.industry),
    // field('Active', boolStr(c?.isActive), boolStr(p?.isActive)), // hidden for now
  ];
}

// Campaign edits that move the dates can carry an `adCleanup` (what happens to the campaign's ads).
// The API fills it from the database, so this is what the approver is really approving.
function adCleanupRows(p) {
  const cleanup = p?.adCleanup;
  const ads = cleanup?.ads;
  if (!Array.isArray(ads) || ads.length === 0) return [];
  const deleteAll = cleanup.mode === 'delete_all';
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  return [
    section('ADS AFFECTED BY THE DATE CHANGE'),
    field(
      'What happens',
      null,
      deleteAll
        ? 'All ads in this campaign are deleted'
        : 'Ads are kept; run dates outside the new campaign dates are removed',
    ),
    ...ads.map((ad) => {
      const removed = ad.removedDates ?? [];
      const kept    = ad.keptDates ?? [];
      const history = ad.historyDates ?? [];   // before today: kept as run history, never changed
      const all     = [...history, ...removed, ...kept];
      const before  = all.length ? `${plural(all.length, 'run date')}: ${summarizeDates(all)}` : '0 run dates';
      const note    = history.length ? `, ${plural(history.length, 'past date')} kept as history (${formatDateRanges(history, 3)})` : '';
      const after   = ad.willBeDeleted
        ? `Deleted${note}`
        : `${plural(kept.length, 'run date')} kept, ${removed.length} removed${note}`;
      return field(ad.name || `Ad #${ad.adId}`, before, after);
    }),
  ];
}

function buildCampaignRows(c, p) {
  const nameOf = (d) => d?.localizedName ?? d?.localizedPairs;
  return [
    section('IDENTITY'),
    ...localizedRows('Name', nameOf(c), nameOf(p)),
    field('Status', c?.status, p?.status),
    section('SCHEDULE'),
    field('Start Date', fmtLocalDate(c?.startDate), fmtLocalDate(p?.startDate)),
    field('End Date',   fmtLocalDate(c?.endDate),   fmtLocalDate(p?.endDate)),
    section('PRICING'),
    field('Daily Rate',        c?.dailyRate,        p?.dailyRate),
    field('Duration Modifier', c?.durationModifier, p?.durationModifier),
    field('Cost / Day',        c?.costPerDay,        p?.costPerDay),
    field('Total Cost',        c?.totalCost,         p?.totalCost),
    ...adCleanupRows(p),
  ];
}

function buildItemRows(c, p, lookups) {
  const l = lookups ?? {};
  const itemTypeName    = (id) => resolve(l.itemType,        id);
  const merchTypeName   = (id) => resolve(l.merchandiseType, id);
  const catName         = (id) => resolve(l.merchandiseCat,  id);
  const ticketTypeName  = (id) => resolve(l.ticketType,      id);
  const discTypeName    = (id) => resolve(l.discountType,    id);

  const rows = [
    section('IDENTITY'),
    ...localizedRows('Name',        c?.nameLocalizations,        p?.nameLocalizations),
    ...localizedRows('Description', c?.descriptionLocalizations, p?.descriptionLocalizations),
    field('Item Type',  itemTypeName(c?.itemTypeId),  itemTypeName(p?.itemTypeId)),
    field('Available',  boolStr(c?.isAvailable), boolStr(p?.isAvailable)),
  ];

  if (c?.prices?.length || p?.prices?.length) {
    rows.push(section('PRICING'), ...priceRows(c?.prices, p?.prices, l.currency));
  }

  if (c?.tax || p?.tax || c?.taxIsInclusive !== undefined || p?.taxIsInclusive !== undefined
      || c?.taxCategoryId != null || p?.taxCategoryId != null) {
    rows.push(
      section('TAX'),
      field('Tax Category', taxCategoryName(c, l.taxCategory), taxCategoryName(p, l.taxCategory)),
      field('Tax Rate', taxRate(c, l.taxCategories), taxRate(p, l.taxCategories)),
      field('Tax Mode', taxMode(c), taxMode(p)),
    );
  }

  if (c?.merchandise || p?.merchandise) {
    rows.push(
      section('MERCHANDISE'),
      field('Merch Type',     c?.merchandise?.merchandiseTypeName     ?? merchTypeName(c?.merchandise?.merchandiseTypeId),
                              p?.merchandise?.merchandiseTypeName     ?? merchTypeName(p?.merchandise?.merchandiseTypeId)),
      field('Category',       c?.merchandise?.merchandiseCategoryName ?? catName(c?.merchandise?.merchandiseCategoryId),
                              p?.merchandise?.merchandiseCategoryName ?? catName(p?.merchandise?.merchandiseCategoryId)),
      field('Position',       c?.merchandise?.position,    p?.merchandise?.position),
      field('Interaction',    c?.merchandise?.interaction, p?.merchandise?.interaction),
      field('Asset URL',      c?.merchandise?.assetUrl,    p?.merchandise?.assetUrl),
      // Current (live) snapshots nest it as `.thumbnail`; proposed snapshots (built from the
      // edit form's update-DTO shape) store it as `.thumbnailMedia` — check both.
      ...mediaRows('Thumbnail',
        c?.merchandise?.thumbnail ?? c?.merchandise?.thumbnailMedia,
        p?.merchandise?.thumbnail ?? p?.merchandise?.thumbnailMedia),
    );
  }

  if (c?.ticket || p?.ticket) {
    rows.push(
      section('TICKET'),
      field('Ticket Type',   c?.ticket?.ticketTypeName ?? ticketTypeName(c?.ticket?.ticketTypeId),
                              p?.ticket?.ticketTypeName ?? ticketTypeName(p?.ticket?.ticketTypeId)),
      field('Event ID',      c?.ticket?.eventId,      p?.ticket?.eventId),
      field('Seat',          c?.ticket?.seat,         p?.ticket?.seat),
      field('Section',       c?.ticket?.section,      p?.ticket?.section),
      field('Valid From',    fmtLocalDate(c?.ticket?.validFrom), fmtLocalDate(p?.ticket?.validFrom)),
      field('Valid To',      fmtLocalDate(c?.ticket?.validTo),   fmtLocalDate(p?.ticket?.validTo)),
      field('Transferable',  boolStr(c?.ticket?.isTransferable), boolStr(p?.ticket?.isTransferable)),
      // Same current/proposed shape ambiguity as Merchandise's thumbnail above: live
      // snapshots nest it as `.thumbnail`, proposed (update-DTO shaped) snapshots as `.thumbnailMedia`.
      ...mediaRows('Thumbnail',
        c?.ticket?.thumbnail ?? c?.ticket?.thumbnailMedia,
        p?.ticket?.thumbnail ?? p?.ticket?.thumbnailMedia),
    );
  }

  if (c?.badge || p?.badge) {
    rows.push(
      section('BADGE'),
      ...mediaRows('Badge Image', c?.badge?.media, p?.badge?.media),
    );
  }

  if (c?.avatar || p?.avatar) {
    rows.push(
      section('AVATAR'),
      field('Asset URL', c?.avatar?.assetUrl,        p?.avatar?.assetUrl),
      // Same current/proposed shape ambiguity as Merchandise's thumbnail above.
      ...mediaRows('Thumbnail',
        c?.avatar?.thumbnail ?? c?.avatar?.thumbnailMedia,
        p?.avatar?.thumbnail ?? p?.avatar?.thumbnailMedia),
    );
  }

  // ItemDiscountOut: { discountTypeId, ratePct, amount, startAt, endAt }
  if (c?.discounts?.length || p?.discounts?.length) {
    rows.push(section('DISCOUNTS'));
    const maxLen = Math.max(c?.discounts?.length ?? 0, p?.discounts?.length ?? 0);
    for (let i = 0; i < maxLen; i++) {
      const cd  = c?.discounts?.[i];
      const pd  = p?.discounts?.[i];
      const typeId   = cd?.discountTypeId ?? pd?.discountTypeId;
      const typeName = discTypeName(typeId);
      const lbl = `Discount ${i + 1}${typeName ? ` (${typeName})` : ''}`;
      const fmt = (x) => {
        if (x == null) return null;
        if (x.ratePct != null) return `${x.ratePct}% off`;
        if (x.amount  != null) return `${x.amount} flat`;
        return '—';
      };
      rows.push(field(lbl, fmt(cd), fmt(pd)));
    }
  }

  return rows;
}

function buildDiscountRows(c, p, lookups) {
  const discTypeName = (id) => resolve(lookups?.discountType, id);
  const valOf = (d) => {
    if (d == null) return null;
    if (d.ratePct != null) return `${d.ratePct}% off`;
    if (d.amount  != null) return `${d.amount} flat`;
    return null;
  };
  return [
    section('IDENTITY'),
    ...localizedRows('Name', c?.nameLocalizations, p?.nameLocalizations),
    field('Discount Type', discTypeName(c?.discountTypeId), discTypeName(p?.discountTypeId)),
    field('Value', valOf(c), valOf(p)),
    section('SCHEDULE'),
    field('Start Date', fmtLocalDate(c?.startAt), fmtLocalDate(p?.startAt)),
    field('End Date',   fmtLocalDate(c?.endAt),   fmtLocalDate(p?.endAt)),
  ];
}

function buildBoothMediaRows(c, p, lookups) {
  const mediaTypeName = (id) => resolve(lookups?.mediaType, id);

  return [
    section('MEDIA DETAILS'),
    field('Media Type', mediaTypeName(c?.mediaTypeId), mediaTypeName(p?.mediaTypeId)),
    ...localizedRows('Title',       c?.textLocalizations,        p?.textLocalizations),
    ...localizedRows('Description', c?.descriptionLocalizations, p?.descriptionLocalizations),
    ...mediaRows('Media URL', c, p),
  ];
}

// Run dates are drawn as an aligned grid (see ContentComparisonView.CompareRow), so the row carries the date
// arrays; the count strings stay as the added/removed signal (null = no dates) and as a text fallback.
function datesField(label, rawCurrent, rawProp) {
  const clean = (d) => (Array.isArray(d) && d.length ? [...new Set(d)].sort() : null);
  const count = (d) => (d ? `${d.length} day${d.length === 1 ? '' : 's'}` : null);
  const c = clean(rawCurrent);
  const p = clean(rawProp);
  return {
    type: 'field',
    label,
    currentValue: count(c),
    proposedValue: count(p),
    currentDates: c,
    proposedDates: p,
    isChanged: JSON.stringify(c) !== JSON.stringify(p),
  };
}

const AD_MEDIA_TYPE_LABELS = { 1: 'Image', 2: 'Video', 3: 'Audio', 4: '3D' };

function buildAdRows(c, p, lookups) {
  // The key of the placement a snapshot points at. The placement ID is the truth: a proposal stored before the server
  // refreshed the derived fields can carry the NEW id with the OLD key, which made a placement change look like no change.
  // The live placements are looked up by id; a placement that no longer exists (a removed one) keeps the key stored with
  // the snapshot, which is exactly what the "current" side needs.
  const livePlacementKeys = lookups?.adPlacement;
  const placementKey = (d) => (d?.placementId != null && livePlacementKeys?.[d.placementId]) || d?.placementKey;

  // AdData.mediaLinks is [{localeId, mediaLink}] — wrap to match mediaRows() expectation.
  const wrapLinks = (d) =>
    d?.mediaLinks?.length ? { linkLocalizations: d.mediaLinks } : null;

  const mediaTypeLabel = (id) =>
    id != null ? (AD_MEDIA_TYPE_LABELS[id] ?? String(id)) : null;

  const placementType = (d) => (d?.is3d == null ? null : (d.is3d ? '3D' : '2D'));
  const modelLabel    = (d) => (d?.model ? `${d.model.displayName ?? d.model.modelKey} (${d.model.modelKey})` : null);

  // A 3D ad's portal, side by side (only from a side that IS 3D). Booth and event are not shown: an ad's portal has neither.
  // A 2D ad's portal is just its click link: one "Click Link" row instead of the portal block.
  const cp = c?.is3d ? (c.portal ?? null) : null;
  const pp = p?.is3d ? (p.portal ?? null) : null;
  const hasClickPortal = (d) => Boolean(d && !d.is3d && d.portal);
  const clickLink      = (d) => (hasClickPortal(d) ? (clickLinkFromPortal(d.portal) || null) : null);
  const portalType = (id) => resolve(lookups?.portalType, id);
  const mainMediaType = (portal) => resolve(lookups?.mediaType, portal?.correspondingMedia?.mediaTypeId);
  const portalRows = cp || pp ? [
    section('PORTAL'),
    ...localizedRows('Title', cp?.localizedPairs, pp?.localizedPairs),
    field('Portal Type',     portalType(cp?.portalTypeId), portalType(pp?.portalTypeId)),
    field('Main Media Type', mainMediaType(cp),            mainMediaType(pp)),
    ...mediaRows('Main Media URL', cp?.correspondingMedia, pp?.correspondingMedia),
    ...portalLinkRows(cp, pp),
  ] : [];

  return [
    section('IDENTITY'),
    ...localizedRows('Name', c?.localizedName, p?.localizedName),
    section('AD DETAILS'),
    field('Placement Key',  placementKey(c),   placementKey(p)),
    field('Placement Type', placementType(c),  placementType(p)),
    field('Duration (sec)', c?.durationSeconds, p?.durationSeconds),
    field('Media Type',     mediaTypeLabel(c?.mediaTypeId), mediaTypeLabel(p?.mediaTypeId)),
    ...(c?.is3d || p?.is3d || c?.model || p?.model ? [field('3D Model', modelLabel(c), modelLabel(p))] : []),
    ...mediaRows('Media URL', wrapLinks(c), wrapLinks(p)),
    ...(hasClickPortal(c) || hasClickPortal(p) ? [field('Click Link', clickLink(c), clickLink(p))] : []),
    ...portalRows,
    ...(c?.scheduleDates?.length || p?.scheduleDates?.length ? [
      section('SCHEDULE'),
      datesField('Run Dates', c?.scheduleDates, p?.scheduleDates),
    ] : []),
  ];
}

function buildEventRows(c, p, lookups) {
  const l = lookups ?? {};
  const eventTypeName    = (id) => resolve(l.eventType,    id);
  const platformTypeName = (id) => resolve(l.platformType, id);
  const eventFormatName  = (id) => resolve(l.eventFormat,  id);

  const personalityLabel = (d) =>
    d?.personalityName ?? (d?.personalityId != null ? String(d.personalityId) : null);

  return [
    section('IDENTITY'),
    ...localizedRows('Name',        c?.localizedName,        p?.localizedName),
    ...localizedRows('Description', c?.localizedDescription, p?.localizedDescription),

    section('CLASSIFICATION'),
    field('Event Type',    eventTypeName(c?.eventTypeId),       eventTypeName(p?.eventTypeId)),
    field('Platform Type', platformTypeName(c?.platformTypeId), platformTypeName(p?.platformTypeId)),
    field('Format',        eventFormatName(c?.eventFormatId),   eventFormatName(p?.eventFormatId)),
    field('Personality',   personalityLabel(c),                 personalityLabel(p)),
    ...(c?.personalityPhotoLink || p?.personalityPhotoLink ? [
      field('Personality Photo', c?.personalityPhotoLink, p?.personalityPhotoLink),
    ] : []),
    field('Event URL', c?.eventUrl, p?.eventUrl),

    section('SCHEDULE'),
    field('Start',           fmtEventDateTime(c?.scheduledStart), fmtEventDateTime(p?.scheduledStart)),
    field('End',             fmtEventDateTime(c?.scheduledEnd),   fmtEventDateTime(p?.scheduledEnd)),
    field('Length (min)',    c?.scheduledLength, p?.scheduledLength),
    field('Live / Recorded', liveRecordedStr(c?.isLive), liveRecordedStr(p?.isLive)),

    section('CAPACITY'),
    field('Capacity', c?.capacity ?? 'Unlimited', p?.capacity ?? 'Unlimited'),

    section('HOSTING'),
    field('Stream URL', c?.hostingStreamUrl, p?.hostingStreamUrl),
    field('Subtitles',  enabledDisabledStr(c?.subtitleEnabled), enabledDisabledStr(p?.subtitleEnabled)),
  ];
}

function buildRecognitionRows(c, p) {
  return [
    section('CONTENT'),
    ...localizedRows('Comment Text', c?.localizedPairs, p?.localizedPairs),
    // section('STATUS'),
    // field('Active', boolStr(c?.isActive), boolStr(p?.isActive)), // hidden for now
  ];
}

// optionType ("calendar") is already a plain human-readable string resolved server-side —
// shown directly, no id+lookup round trip needed (unlike portalTypeName/mediaTypeName above).
// Links use the exact same position-name resolution buildPortalRows already uses (Left/
// Center/Right by positionId), since custom items now support up to 3 positioned links
// mirroring Portal's own portal_link.
function buildCustomBoothItemRows(c, p) {
  const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : null);

  const allLinkKeys = () => {
    const seen = new Set();
    const result = [];
    for (const l of [...(c?.links ?? []), ...(p?.links ?? [])]) {
      const key = l?.positionId ?? l?.id;
      if (key != null && !seen.has(key)) { seen.add(key); result.push(key); }
    }
    return result.sort((a, b) => a - b);
  };

  const linkRows = allLinkKeys().flatMap((key) => {
    const cl  = (c?.links ?? []).find((l) => (l.positionId ?? l.id) === key);
    const pl  = (p?.links ?? []).find((l) => (l.positionId ?? l.id) === key);
    const pos = cl?.positionId ?? pl?.positionId ?? key;
    const posName = portalPositionName(pos);
    return [
      field(`${posName} URL`, cl?.externalLink ?? null, pl?.externalLink ?? null),
      ...mediaRows(`${posName} Thumbnail`, cl?.thumbnailMedia, pl?.thumbnailMedia),
    ];
  });

  return [
    section('IDENTITY'),
    field('Option Type', titleCase(c?.optionType), titleCase(p?.optionType)),
    ...localizedRows('Name', c?.localizedPairs, p?.localizedPairs),
    ...localizedRows('Description', c?.descriptionPairs, p?.descriptionPairs),
    section('MEDIA'),
    ...mediaRows('Image', c?.correspondingMedia, p?.correspondingMedia),
    ...(linkRows.length > 0 ? [section('LINKS'), ...linkRows] : []),
    section('CALENDAR'),
    field('Start', fmtEventDateTime(c?.startDateTime), fmtEventDateTime(p?.startDateTime)),
    field('End',   fmtEventDateTime(c?.endDateTime),   fmtEventDateTime(p?.endDateTime)),
  ];
}

// ── Package ──────────────────────────────────────────────────────────────────

const usableLocales = (d) => toLocaleEntries(d).filter((e) => e?.localeId && String(e.value ?? '').trim() !== '');

export function formatMinor(minor, currencyId, lookups) {
  if (minor == null) return null;
  const cur = (lookups?.currencyList ?? []).find((x) => x.id === currencyId);
  const decimals = cur?.DecimalPlaces ?? 2;
  const amount = (Number(minor) / 10 ** decimals).toFixed(decimals);
  if (!cur) return currencyId != null ? `${amount} (currency ${currencyId})` : amount;
  return `${cur.Symbol ?? ''}${amount} ${cur.ISO ?? ''}`.trim();
}

function packageTax(d, lookups) {
  const id = d?.taxCategoryId ?? d?.tax?.categoryId ?? null;
  if (id == null) return null;
  const nested = d?.tax?.categoryId === id ? d.tax : null;
  const category = (lookups?.taxCategories ?? []).find((t) => t.id === id);
  return {
    name: nested?.name ?? category?.name ?? `Category ${id}`,
    rate: nested?.ratePct ?? category?.ratePct ?? null,
    inclusive: d?.taxIsInclusive ?? d?.tax?.isInclusive ?? null,
  };
}

// What the customer pays: the base cost, plus the tax when it is not already included (same rule as Packages.jsx).
export function customerPrice(baseCost, tax) {
  if (baseCost == null) return null;
  if (!tax || tax.inclusive) return Number(baseCost);
  if (tax.rate == null) return null;   // rate unknown (lookup not loaded): better no total than a wrong one
  return Math.round(Number(baseCost) * (1 + tax.rate / 100));
}

// One package member as a row: its name as the label (with its picture and "type · price" under it), its quantity
// as the value. `currentOn` / `proposedOn` say which side it is on — one side only reads as added or removed.
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function memberRow(m, currentOn, proposedOn, lookups) {
  const qty = `× ${m?.quantity ?? 1}`;
  const price = m?.standalonePrice ? formatMinor(m.standalonePrice.baseCost, m.standalonePrice.currencyId, lookups) : null;
  return {
    ...field(m?.name ?? `Item ${m?.itemId}`, currentOn ? qty : null, proposedOn ? qty : null),
    thumb: m?.thumbnailUrl ?? null,
    sub: [m?.itemTypeName, price].filter(Boolean).join(' · ') || null,
  };
}

function buildPackageRows(c, p, lookups) {
  const keep = (proposed, current) => (proposed == null ? current : proposed);

  const cBase = c?.baseCost ?? c?.prices?.[0]?.baseCost ?? null;
  const pBase = p?.baseCost ?? p?.prices?.[0]?.baseCost ?? cBase;
  const cCurrency = c?.currencyId ?? c?.prices?.[0]?.currencyId ?? null;
  const pCurrency = p?.currencyId ?? p?.prices?.[0]?.currencyId ?? cCurrency;

  const cTax = packageTax(c, lookups);
  const pTax = packageTax(p, lookups) ?? cTax;

  const cThumb = c?.thumbnailMedia ?? c?.thumbnail;
  const pThumb = p?.thumbnailMedia ?? p?.thumbnail ?? cThumb;
  // `preview` makes the compare grid draw the picture itself under the URL.
  const thumbRows = mediaRows('Thumbnail', cThumb, pThumb).map((row) => ({ ...row, preview: true }));

  return [
    section('IDENTITY'),
    ...localizedRows('Name', usableLocales(c?.nameLocalizations), usableLocales(keep(p?.nameLocalizations, c?.nameLocalizations))),
    ...localizedRows('Description', usableLocales(c?.descriptionLocalizations), usableLocales(keep(p?.descriptionLocalizations, c?.descriptionLocalizations))),
    ...(thumbRows.length > 0 ? [section('THUMBNAIL'), ...thumbRows] : []),
    section('PRICING'),
    field('Base Price', formatMinor(cBase, cCurrency, lookups), formatMinor(pBase, pCurrency, lookups)),
    field('Tax Category', cTax?.name ?? 'None', pTax?.name ?? 'None'),
    field('Tax Rate', cTax?.rate != null ? `${cTax.rate}%` : null, pTax?.rate != null ? `${pTax.rate}%` : null),
    field('Tax Mode', cTax?.inclusive == null ? null : (cTax.inclusive ? 'Inclusive' : 'Exclusive'), pTax?.inclusive == null ? null : (pTax.inclusive ? 'Inclusive' : 'Exclusive')),
    field('Customer Price (with tax)', formatMinor(customerPrice(cBase, cTax), cCurrency, lookups), formatMinor(customerPrice(pBase, pTax), pCurrency, lookups)),
    section('AVAILABILITY'),
    field('Available', boolStr(c?.isAvailable), boolStr(keep(p?.isAvailable, c?.isAvailable))),
    // An edit never changes the members (they have their own approvals) — listed so the approver knows what the
    // package holds.
    ...(Array.isArray(c?.members) && c.members.length > 0
      ? [section('ITEMS IN PACKAGE'), ...c.members.map((m) => memberRow(m, true, true, lookups))]
      : []),
  ];
}

// FreeTrialController.UpdateSetting overlays the request the same way, and every field of the request is optional
// (null = unchanged), so a missing proposed value falls back to the live one.
function buildFreeTrialSettingRows(c, p) {
  const keep = (proposed, current) => (proposed == null ? current : proposed);
  return [
    section('CAMPAIGN WINDOW'),
    field('Start Date', fmtLocalDate(c?.startAt), fmtLocalDate(keep(p?.startAt, c?.startAt))),
    field('End Date',   fmtLocalDate(c?.endAt),   fmtLocalDate(keep(p?.endAt, c?.endAt))),
    section('STATUS'),
    field('Enabled', boolStr(c?.isEnabled), boolStr(keep(p?.isEnabled, c?.isEnabled))),
  ];
}

// ── Membership: a package's member and a free-trial list entry ───────────────

const titleCase = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);
const note = (tone, text) => ({ type: 'note', tone, text });

function buildPackageItemRows(added, s, lookups) {
  const money = (p) => (p ? formatMinor(p.baseCost, p.currencyId, lookups) : null);
  const self = {
    packageItemId: s.packageItemId,
    itemId: s.itemId,
    name: s.name,
    quantity: s.quantity,
    itemTypeName: s.itemTypeName,
    standalonePrice: s.standalonePrice,
    thumbnailUrl: s.thumbnailUrl,
  };
  const members = Array.isArray(s.packageMembers) ? s.packageMembers : [];

  // Approvals submitted before the package was recorded: all that is known is the member itself.
  if (!s.packageName && members.length === 0) {
    return [
      section('PACKAGE MEMBER'),
      memberRow(self, !added, added, lookups),
      note('info', 'The package this item belongs to was not recorded with this approval.'),
    ];
  }

  const others = members.filter((m) => m.packageItemId !== self.packageItemId);
  const before = others.length + (added ? 0 : 1);
  const after  = others.length + (added ? 1 : 0);
  // The package's own order, the changed member at its place.
  const ordered = members.some((m) => m.packageItemId === self.packageItemId) ? members : [...members, self];
  const packageName = s.packageName ?? `Package ${s.packageId}`;

  return [
    section('PACKAGE'),
    { ...field('Name', packageName, packageName), thumb: s.packageThumbnailUrl ?? null },
    ...(s.packagePrice ? [field('Package Price', money(s.packagePrice), money(s.packagePrice))] : []),
    section('ITEMS IN PACKAGE'),
    field('Items', plural(before, 'item'), plural(after, 'item')),
    ...ordered.map((m) => (m.packageItemId === self.packageItemId
      ? memberRow(self, !added, added, lookups)
      : memberRow(m, true, true, lookups))),
  ];
}

function buildFreeTrialItemRows(added, s, lookups) {
  const isPackage = s.packageId != null;
  const name = isPackage
    ? (s.packageName ?? `Package ${s.packageId}`)
    : (s.itemName ?? s.displayName ?? (s.itemId != null ? `Item ${s.itemId}` : null));
  // [current, proposed]: an add exists only on the proposed side, a remove only on the current one.
  const side = (label, value) => field(label, added ? null : value, added ? value : null);

  const typeName = !isPackage && s.itemTypeId != null ? resolve(lookups?.itemType, s.itemTypeId) : null;
  const price = s.price ? formatMinor(s.price.baseCost, s.price.currencyId, lookups) : null;

  const rows = [
    section(isPackage ? 'TRIAL PACKAGE' : 'TRIAL ITEM'),
    { ...side('Name', name), thumb: s.thumbnailUrl ?? null },
    ...(typeName ? [side('Type', typeName)] : []),
    ...(price ? [side('Price', price)] : []),
    ...(isPackage && s.packageMemberCount != null ? [side('Items inside', plural(s.packageMemberCount, 'item'))] : []),
  ];


  const campaign = s.campaign;
  if (campaign !== undefined) {
    rows.push(section('CAMPAIGN'));
    if (campaign) {
      const window = `${fmtLocalDate(campaign.startAt)} – ${fmtLocalDate(campaign.endAt)}`;
      const state  = `${campaign.isEnabled ? 'Enabled' : 'Disabled'} · ${titleCase(campaign.status ?? 'unknown')}`;
      rows.push(field('Window', window, window), field('Status', state, state));
    } else {
      rows.push(field('Campaign', 'None yet', 'None yet'));
    }
  }

  if (s.trialListLiveCount != null) {
    // the list holds items and packages alike, so it is counted as entries
    const entries = (n) => `${n} ${n === 1 ? 'entry' : 'entries'}`;
    const count = s.trialListLiveCount;
    rows.push(
      section('TRIAL LIST'),
      field('Live on the list', entries(count), entries(Math.max(0, count + (added ? 1 : -1)))),
    );
  }

  const live = Boolean(campaign && campaign.isEnabled && campaign.status === 'approved');
  if (added && campaign !== undefined && !live) {
    rows.push(note('warning', `The campaign is not enabled and approved yet — ${isPackage ? 'this package' : 'this item'} cannot be claimed until it is.`));
  }
  if (added && campaign) {
    rows.push(note('info', `Customers will be able to claim a free trial of "${name}" from ${fmtLocalDate(campaign.startAt)} to ${fmtLocalDate(campaign.endAt)}.`));
  }
  if (!added) {
    rows.push(note('info', `Customers will no longer be able to claim a free trial of "${name}".`));
  }
  return rows;
}

export function buildMembershipRows(contentType, actionType, snapshot, lookups) {
  if (!snapshot) return [];
  const added = String(actionType ?? '').toLowerCase() !== 'delete';
  switch (String(contentType ?? '').toLowerCase()) {
    case 'package_item':    return buildPackageItemRows(added, snapshot, lookups);
    case 'free_trial_item': return buildFreeTrialItemRows(added, snapshot, lookups);
    default:                return [];
  }
}

// ── Registry & export ────────────────────────────────────────────────────────

function buildTeleportButtonRows(c, p) {
  const currentSpotId  = c.mapSpot?.id ?? c.mapSpotId ?? null;
  const proposedSpotId = p.mapSpot?.id ?? p.mapSpotId ?? null;

  return [
    // Special row type — rendered as the mini stadium visual in ContentComparisonView
    { type: 'mapVisual', currentSpotId, proposedSpotId },
    section('Button Name'),
    ...localizedRows('Name', c.localizedPairs, p.localizedPairs),
  ];
}

// ── Game schedule (the Quiz) ─────────────────────────────────────────────────

const sortedBySortOrder = (list) => [...(Array.isArray(list) ? list : [])].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));

// Pairs the rows of two lists by id: a proposed row with a current row's id is the same row (edited or not); one with no
// match — a new question or option has no id yet — is added; a current row nobody claims is removed. Proposed order
// first, removed rows after it. Each pair carries the positions the row has on its side(s).
function pairById(currentList, proposedList) {
  const current = sortedBySortOrder(currentList);
  const proposed = sortedBySortOrder(proposedList);
  const claimed = new Set();

  const pairs = proposed.map((p, pi) => {
    const ci = p?.id != null ? current.findIndex((c) => c?.id === p.id) : -1;
    if (ci >= 0) claimed.add(ci);
    return { current: ci >= 0 ? current[ci] : null, proposed: p, ci: ci >= 0 ? ci : null, pi };
  });
  current.forEach((c, ci) => {
    if (!claimed.has(ci)) pairs.push({ current: c, proposed: null, ci, pi: null });
  });
  return pairs;
}

const primaryLocaleText = (text) => {
  const values = toLocaleEntries(text);
  return (values.find((e) => e?.localeId === 'en_us') ?? values[0])?.value ?? null;
};

const correctOptionText = (question) =>
  primaryLocaleText((question?.options ?? []).find((o) => o?.isCorrect)?.text);

function buildQuizRows(c, p) {
  const typeLabel = (d) => (d?.scheduleType ? (SCHEDULE_TYPE_LABEL[String(d.scheduleType).toLowerCase()] ?? d.scheduleType) : null);

  const rows = [
    section('PERIOD'),
    field('Name', c?.name, p?.name),
    field('Period', typeLabel(c), typeLabel(p)),
    field('Start Date', fmtLocalDate(c?.startDate), fmtLocalDate(p?.startDate)),
    field('End Date', fmtLocalDate(c?.endDate), fmtLocalDate(p?.endDate)),
  ];

  // The live announcement linked to the quiz follows its new dates (and gets the chosen announcement date) when this
  // edit is approved, so the approver sees it next to the quiz change.
  const ca = c?.linkedAnnouncement;
  const pa = p?.linkedAnnouncement;
  if (ca || pa) {
    rows.push(
      section('LINKED ANNOUNCEMENT'),
      field('Announcement', ca?.name, pa?.name),
      field('Start Date', fmtLocalDate(ca?.startDate), fmtLocalDate(pa?.startDate)),
      field('End Date', fmtLocalDate(ca?.endDate), fmtLocalDate(pa?.endDate)),
      field('Announcement Date', fmtLocalDate(ca?.announcementDate), fmtLocalDate(pa?.announcementDate)),
    );
  }

  pairById(c?.questions, p?.questions).forEach(({ current: cq, proposed: pq, ci, pi }) => {
    const tag = !cq ? ' (new)' : (!pq ? ' (removed)' : '');
    rows.push(section(`QUESTION ${(pi ?? ci) + 1}${tag}`));
    if (cq && pq && ci !== pi) rows.push(field('Position', ci + 1, pi + 1));

    rows.push(...localizedRows('Question', cq?.text, pq?.text));

    pairById(cq?.options, pq?.options).forEach(({ current: co, proposed: po, ci: oci, pi: opi }) => {
      rows.push(...localizedRows(`Option ${(opi ?? oci) + 1}`, co?.text, po?.text));
    });

    rows.push(field('Correct answer', correctOptionText(cq), correctOptionText(pq)));
  });

  return rows;
}

const ROW_BUILDERS = {
  booth:            buildBoothRows,
  booth_media:      buildBoothMediaRows,
  portal:           buildPortalRows,
  charity:          buildCharityRows,
  hosting:          buildHostingRows,
  advertiser:       buildAdvertiserRows,
  campaign:         buildCampaignRows,
  item:             buildItemRows,
  discount:         buildDiscountRows,
  ad:               buildAdRows,
  teleport_button:  buildTeleportButtonRows,
  recognition:      buildRecognitionRows,
  event:            buildEventRows,
  custom_booth_item: buildCustomBoothItemRows,
  package:            buildPackageRows,
  free_trial_setting: buildFreeTrialSettingRows,
  game_schedule:      buildQuizRows,
};

export function buildComparisonRows(contentType, current, proposed, lookups) {
  const builder = ROW_BUILDERS[contentType?.toLowerCase()];
  if (!builder) return [];
  return builder(current ?? {}, proposed ?? {}, lookups).filter(Boolean);
}
